import React, { useState, useEffect } from 'react';
import {
  Warehouse,
  Search,
  Plus,
  RefreshCw,
  Package,
  Calendar,
  Sprout,
  CheckCircle,
  X,
  Edit,
  Trash2,
  Eye,
  Send,
  Clock,
  Leaf,
  ClipboardList,
  User,
  Phone
} from 'lucide-react';
import { Plant, InventoryItem, InventoryStatus, InventoryDispenseRecord } from '../../types';
import { plantService, inventoryService } from '../../services/firebase';
import { Timestamp } from 'firebase/firestore';

const Inventory: React.FC = () => {
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDispenseModal, setShowDispenseModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [dispenseRecords, setDispenseRecords] = useState<InventoryDispenseRecord[]>([]);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);

  // Tab state: 'inventory' or 'dispense'
  const [activeTab, setActiveTab] = useState<'inventory' | 'dispense'>('inventory');
  const [allDispenseRecords, setAllDispenseRecords] = useState<InventoryDispenseRecord[]>([]);
  const [dispenseSearchTerm, setDispenseSearchTerm] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    plantId: '',
    totalTrays: '',
    seedingDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Dispense form states
  const [dispenseData, setDispenseData] = useState({
    traysToDispense: '',
    customerName: '',
    customerPhone: '',
    notes: ''
  });

  // New plant form states (inline)
  const [showAddPlantForm, setShowAddPlantForm] = useState(false);
  const [newPlantData, setNewPlantData] = useState({
    name: '',
    brand: '',
    variety: '',
    pricePerPlant: '',
    growthDuration: '',
    seedsPerTray: '126',
    seedsProvider: 'Company' as 'Farmer' | 'Company',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [items, plantsData, dispenseData] = await Promise.all([
        inventoryService.getAllInventoryItems(),
        plantService.getAllPlants(),
        inventoryService.getAllDispenseRecords()
      ]);
      setInventoryItems(items);
      setPlants(plantsData);
      setAllDispenseRecords(dispenseData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = inventoryItems.filter(item => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      item.plantName.toLowerCase().includes(search) ||
      (item.plantBrand && item.plantBrand.toLowerCase().includes(search)) ||
      (item.plantVariety && item.plantVariety.toLowerCase().includes(search));

    const matchesStatus = statusFilter === 'all' || item.status.name.toLowerCase().replace(' ', '_') === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleCreateInventory = async (e: React.FormEvent) => {
    e.preventDefault();

    let selectedPlant = plants.find(p => p.id === formData.plantId);

    // Create new plant if adding inline
    if (showAddPlantForm) {
      if (!newPlantData.name || !newPlantData.brand || !newPlantData.variety ||
          !newPlantData.pricePerPlant || !newPlantData.growthDuration || !newPlantData.seedsPerTray) {
        alert('Please fill in all required plant details.');
        return;
      }

      try {
        const plantData = {
          name: newPlantData.name,
          brand: newPlantData.brand,
          variety: newPlantData.variety,
          pricePerPlant: parseFloat(newPlantData.pricePerPlant),
          growthDuration: parseInt(newPlantData.growthDuration),
          seedsPerTray: parseInt(newPlantData.seedsPerTray),
          seedsProvider: newPlantData.seedsProvider,
          availability: 0,
          isActive: true,
        };
        const newPlantId = await plantService.addPlant(plantData);

        selectedPlant = {
          id: newPlantId,
          ...plantData,
          createdAt: new Date() as any,
        };
        setPlants([selectedPlant, ...plants]);
      } catch (error) {
        console.error('Error creating plant:', error);
        alert('Error creating plant. Please try again.');
        return;
      }
    }

    if (!selectedPlant || !formData.totalTrays || !formData.seedingDate) {
      alert('Please select a plant and enter required details.');
      return;
    }

    const totalTrays = parseInt(formData.totalTrays);
    const totalPlants = totalTrays * selectedPlant.seedsPerTray;
    const seedingDate = new Date(formData.seedingDate);
    const expectedReadyDate = new Date(seedingDate);
    expectedReadyDate.setDate(expectedReadyDate.getDate() + selectedPlant.growthDuration);

    const inventoryData: Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'> = {
      plantId: selectedPlant.id,
      plantName: selectedPlant.name,
      plantBrand: selectedPlant.brand,
      plantVariety: selectedPlant.variety,
      seedsPerTray: selectedPlant.seedsPerTray,
      totalTrays,
      totalPlants,
      availableTrays: totalTrays,
      availablePlants: totalPlants,
      seedingDate: Timestamp.fromDate(seedingDate),
      expectedReadyDate: Timestamp.fromDate(expectedReadyDate),
      status: { id: '1', name: 'Seeding', description: 'Recently seeded' },
      notes: formData.notes || undefined
    };

    try {
      if (editingItem) {
        await inventoryService.updateInventoryItem(editingItem.id, inventoryData);
      } else {
        await inventoryService.addInventoryItem(inventoryData);
      }
      await fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving inventory item:', error);
      alert('Error saving inventory item. Please try again.');
    }
  };

  const handleDispense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem || !dispenseData.traysToDispense) return;

    const trays = parseInt(dispenseData.traysToDispense);
    if (trays > selectedItem.availableTrays) {
      alert(`Cannot dispense ${trays} trays. Only ${selectedItem.availableTrays} available.`);
      return;
    }

    try {
      await inventoryService.dispenseFromInventory(
        selectedItem.id,
        trays,
        dispenseData.customerName || undefined,
        dispenseData.customerPhone || undefined,
        dispenseData.notes || undefined
      );
      await fetchData();
      setShowDispenseModal(false);
      setSelectedItem(null);
      setDispenseData({ traysToDispense: '', customerName: '', customerPhone: '', notes: '' });
    } catch (error) {
      console.error('Error dispensing:', error);
      alert('Error dispensing from inventory. Please try again.');
    }
  };

  const handleDelete = async (item: InventoryItem) => {
    if (!window.confirm(`Are you sure you want to delete this inventory item for "${item.plantName}"?`)) {
      return;
    }

    try {
      await inventoryService.deleteInventoryItem(item.id);
      await fetchData();
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      alert('Error deleting inventory item. Please try again.');
    }
  };

  const handleEdit = (item: InventoryItem) => {
    setEditingItem(item);
    const seedingDate = (item.seedingDate as any)?.toDate
      ? (item.seedingDate as any).toDate()
      : new Date(item.seedingDate as any);

    setFormData({
      plantId: item.plantId,
      totalTrays: item.totalTrays.toString(),
      seedingDate: seedingDate.toISOString().split('T')[0],
      notes: item.notes || ''
    });
    setShowCreateForm(true);
  };

  const handleViewDetails = async (item: InventoryItem) => {
    setSelectedItem(item);
    try {
      const records = await inventoryService.getDispenseRecords(item.id);
      setDispenseRecords(records);
    } catch (error) {
      console.error('Error fetching dispense records:', error);
    }
    setShowDetailsModal(true);
  };

  const openDispenseModal = (item: InventoryItem) => {
    setSelectedItem(item);
    setDispenseData({ traysToDispense: '', customerName: '', customerPhone: '', notes: '' });
    setShowDispenseModal(true);
  };

  const resetForm = () => {
    setFormData({ plantId: '', totalTrays: '', seedingDate: new Date().toISOString().split('T')[0], notes: '' });
    setNewPlantData({ name: '', brand: '', variety: '', pricePerPlant: '', growthDuration: '', seedsPerTray: '126', seedsProvider: 'Company' });
    setShowAddPlantForm(false);
    setShowCreateForm(false);
    setEditingItem(null);
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '-';
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN');
  };

  const getStatusColor = (status: InventoryStatus) => {
    switch (status.name) {
      case 'Seeding':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      case 'Growing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'Ready':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'Partially Dispensed':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'Fully Dispensed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: InventoryStatus) => {
    switch (status.name) {
      case 'Seeding':
        return <Sprout className="h-4 w-4" />;
      case 'Growing':
        return <Leaf className="h-4 w-4" />;
      case 'Ready':
        return <CheckCircle className="h-4 w-4" />;
      case 'Partially Dispensed':
        return <Send className="h-4 w-4" />;
      case 'Fully Dispensed':
        return <Package className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  // Stats
  const totalTrays = inventoryItems.reduce((sum, item) => sum + item.availableTrays, 0);
  const totalDispensed = allDispenseRecords.reduce((sum, r) => sum + r.plantsDispensed, 0);

  // Helper to get plant price
  const getPlantPrice = (plantId: string): number => {
    const plant = plants.find(p => p.id === plantId);
    return plant?.pricePerPlant || 0;
  };

  // Helper to get inventory item details for dispense record
  const getInventoryDetails = (inventoryItemId: string) => {
    return inventoryItems.find(item => item.id === inventoryItemId);
  };

  // Filtered dispense records
  const filteredDispenseRecords = allDispenseRecords.filter(record => {
    const search = dispenseSearchTerm.toLowerCase();
    const inventoryItem = getInventoryDetails(record.inventoryItemId);
    const matchesSearch =
      (record.customerName?.toLowerCase().includes(search)) ||
      (record.customerPhone?.toLowerCase().includes(search)) ||
      (inventoryItem?.plantName.toLowerCase().includes(search)) ||
      (inventoryItem?.plantBrand?.toLowerCase().includes(search)) ||
      (inventoryItem?.plantVariety?.toLowerCase().includes(search));
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Warehouse className="h-5 w-5 text-white" />
            </div>
            Inventory Management
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage nursery's own seedlings</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-colors font-medium"
          >
            <Plus className="h-4 w-4" />
            Create Inventory
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Package className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{inventoryItems.length}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Batches</h3>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <Warehouse className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{totalTrays.toLocaleString()}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Available Trays</h3>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('inventory')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'inventory'
              ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Warehouse className="h-4 w-4" />
          Inventory ({inventoryItems.length})
        </button>
        <button
          onClick={() => setActiveTab('dispense')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'dispense'
              ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <ClipboardList className="h-4 w-4" />
          Dispense Records ({allDispenseRecords.length})
        </button>
      </div>

      {activeTab === 'inventory' && (
      <>
      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by plant name, brand, or variety..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Status</option>
            <option value="seeding">Seeding</option>
            <option value="growing">Growing</option>
            <option value="ready">Ready</option>
            <option value="partially_dispensed">Partially Dispensed</option>
            <option value="fully_dispensed">Fully Dispensed</option>
          </select>
        </div>
      </div>

      {/* Inventory List */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Plant</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Price/Plant</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Trays</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Available</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Seeding Date</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Ready Date</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Status</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <RefreshCw className="h-8 w-8 animate-spin text-indigo-500 mx-auto mb-3" />
                    <p className="text-gray-500">Loading inventory...</p>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <Warehouse className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No inventory items found</p>
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="mt-4 text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      Create your first inventory batch
                    </button>
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 dark:text-white">{item.plantName}</div>
                      <div className="text-sm text-gray-500">
                        {item.plantBrand}{item.plantVariety ? ` - ${item.plantVariety}` : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-semibold text-green-600 dark:text-green-400">₹{getPlantPrice(item.plantId).toFixed(2)}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-semibold text-gray-900 dark:text-white">{item.totalTrays}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`font-bold ${item.availableTrays > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {item.availableTrays}
                      </span>
                      <span className="text-gray-400 text-sm"> / {item.totalTrays}</span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-600 dark:text-gray-300">
                      {formatDate(item.seedingDate)}
                    </td>
                    <td className="px-6 py-4 text-center text-gray-600 dark:text-gray-300">
                      {formatDate(item.expectedReadyDate)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {getStatusIcon(item.status)}
                        {item.status.name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewDetails(item)}
                          className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {item.availableTrays > 0 && (
                          <button
                            onClick={() => openDispenseModal(item)}
                            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Dispense"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {/* Dispense Records Tab */}
      {activeTab === 'dispense' && (
        <>
          {/* Search for Dispense Records */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by customer name, phone, or plant..."
                  value={dispenseSearchTerm}
                  onChange={(e) => setDispenseSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-900/30 rounded-xl">
                <Send className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <span className="text-purple-700 dark:text-purple-300 font-medium">
                  {totalDispensed.toLocaleString()} plants dispensed
                </span>
              </div>
            </div>
          </div>

          {/* Dispense Records List */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Plant</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Customer</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Trays</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Plants</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Price/Plant</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Total Value</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <RefreshCw className="h-8 w-8 animate-spin text-indigo-500 mx-auto mb-3" />
                        <p className="text-gray-500">Loading dispense records...</p>
                      </td>
                    </tr>
                  ) : filteredDispenseRecords.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No dispense records found</p>
                        <p className="text-sm text-gray-400 mt-1">Records will appear here when plants are dispensed from inventory</p>
                      </td>
                    </tr>
                  ) : (
                    filteredDispenseRecords.map((record) => {
                      const inventoryItem = getInventoryDetails(record.inventoryItemId);
                      const pricePerPlant = inventoryItem ? getPlantPrice(inventoryItem.plantId) : 0;
                      const totalValue = pricePerPlant * record.plantsDispensed;
                      return (
                        <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900 dark:text-white">{formatDate(record.dispenseDate)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {inventoryItem ? (
                              <div>
                                <div className="font-semibold text-gray-900 dark:text-white">{inventoryItem.plantName}</div>
                                <div className="text-sm text-gray-500">
                                  {inventoryItem.plantBrand}{inventoryItem.plantVariety ? ` - ${inventoryItem.plantVariety}` : ''}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Unknown plant</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {record.customerName || record.customerPhone ? (
                              <div>
                                {record.customerName && (
                                  <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                                    <User className="h-4 w-4 text-gray-400" />
                                    {record.customerName}
                                  </div>
                                )}
                                {record.customerPhone && (
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Phone className="h-3 w-3" />
                                    {record.customerPhone}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Walk-in</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="font-bold text-indigo-600 dark:text-indigo-400">{record.traysDispensed}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="font-bold text-green-600 dark:text-green-400">{record.plantsDispensed.toLocaleString()}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-gray-600 dark:text-gray-300">₹{pricePerPlant.toFixed(2)}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="font-bold text-amber-600 dark:text-amber-400">₹{totalValue.toLocaleString()}</span>
                          </td>
                          <td className="px-6 py-4">
                            {record.notes ? (
                              <span className="text-gray-600 dark:text-gray-400 text-sm">{record.notes}</span>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Create/Edit Inventory Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Sprout className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      {editingItem ? 'Edit Inventory' : 'Create Inventory'}
                    </h3>
                    <p className="text-green-100 text-sm">Add seedlings to nursery inventory</p>
                  </div>
                </div>
                <button onClick={resetForm} className="text-white/80 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateInventory} className="p-6 space-y-5">
              {/* Plant Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Plant *</label>
                  {!showAddPlantForm && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddPlantForm(true);
                        setFormData({ ...formData, plantId: '' });
                      }}
                      className="inline-flex items-center text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      <Sprout className="h-4 w-4 mr-1" />
                      Add New Plant
                    </button>
                  )}
                </div>

                {!showAddPlantForm ? (
                  <select
                    required={!showAddPlantForm}
                    value={formData.plantId}
                    onChange={(e) => setFormData({ ...formData, plantId: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Choose a plant</option>
                    {plants.map(plant => (
                      <option key={plant.id} value={plant.id}>
                        {plant.name} ({plant.brand}{plant.variety ? ` - ${plant.variety}` : ''}) - {plant.seedsPerTray} seeds/tray, {plant.growthDuration} days
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-medium text-green-800 dark:text-green-300 flex items-center gap-2">
                        <Sprout className="h-5 w-5" />
                        New Plant Details
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddPlantForm(false);
                          setNewPlantData({ name: '', brand: '', variety: '', pricePerPlant: '', growthDuration: '', seedsPerTray: '126', seedsProvider: 'Company' });
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plant Name *</label>
                        <input
                          type="text"
                          value={newPlantData.name}
                          onChange={(e) => setNewPlantData({ ...newPlantData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="e.g., Cherry Tomato"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brand *</label>
                        <input
                          type="text"
                          value={newPlantData.brand}
                          onChange={(e) => setNewPlantData({ ...newPlantData, brand: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="e.g., Syngenta"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Variety *</label>
                        <input
                          type="text"
                          value={newPlantData.variety}
                          onChange={(e) => setNewPlantData({ ...newPlantData, variety: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="e.g., Hybrid F1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Price per Plant (₹) *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newPlantData.pricePerPlant}
                          onChange={(e) => setNewPlantData({ ...newPlantData, pricePerPlant: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="5.50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Growth Duration (days) *</label>
                        <input
                          type="number"
                          value={newPlantData.growthDuration}
                          onChange={(e) => setNewPlantData({ ...newPlantData, growthDuration: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Seeds per Tray *</label>
                        <input
                          type="number"
                          value={newPlantData.seedsPerTray}
                          onChange={(e) => setNewPlantData({ ...newPlantData, seedsPerTray: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="126"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Seeds Provider *</label>
                        <select
                          value={newPlantData.seedsProvider}
                          onChange={(e) => setNewPlantData({ ...newPlantData, seedsProvider: e.target.value as 'Farmer' | 'Company' })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="Company">Company</option>
                          <option value="Farmer">Farmer</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Number of Trays */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Number of Trays *
                </label>
                <input
                  type="number"
                  min="1"
                  required
                  value={formData.totalTrays}
                  onChange={(e) => setFormData({ ...formData, totalTrays: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                  placeholder="Enter number of trays"
                />
                {formData.plantId && formData.totalTrays && (
                  <p className="mt-2 text-sm text-gray-500">
                    Total plants: {parseInt(formData.totalTrays) * (plants.find(p => p.id === formData.plantId)?.seedsPerTray || 0)}
                  </p>
                )}
              </div>

              {/* Seeding Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Seeding Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.seedingDate}
                  onChange={(e) => setFormData({ ...formData, seedingDate: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                />
                {formData.plantId && formData.seedingDate && (
                  <p className="mt-2 text-sm text-gray-500">
                    Expected ready: {new Date(new Date(formData.seedingDate).getTime() + (plants.find(p => p.id === formData.plantId)?.growthDuration || 0) * 24 * 60 * 60 * 1000).toLocaleDateString('en-IN')}
                  </p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 resize-none"
                  placeholder="Any additional notes..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700"
                >
                  {editingItem ? 'Update Inventory' : 'Create Inventory'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dispense Modal */}
      {showDispenseModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Send className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Dispense Plants</h3>
                    <p className="text-purple-100 text-sm">{selectedItem.plantName}</p>
                  </div>
                </div>
                <button onClick={() => { setShowDispenseModal(false); setSelectedItem(null); }} className="text-white/80 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleDispense} className="p-6 space-y-5">
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 text-center">
                <p className="text-sm text-purple-600 dark:text-purple-300 mb-1">Available Trays</p>
                <p className="text-3xl font-bold text-purple-700 dark:text-purple-200">{selectedItem.availableTrays}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Trays to Dispense *
                </label>
                <input
                  type="number"
                  min="1"
                  max={selectedItem.availableTrays}
                  required
                  value={dispenseData.traysToDispense}
                  onChange={(e) => setDispenseData({ ...dispenseData, traysToDispense: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl"
                  placeholder="Enter number of trays"
                />
                {dispenseData.traysToDispense && (
                  <p className="mt-2 text-sm text-gray-500">
                    Plants: {parseInt(dispenseData.traysToDispense) * selectedItem.seedsPerTray}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Farmer Name (Optional)
                </label>
                <input
                  type="text"
                  value={dispenseData.customerName}
                  onChange={(e) => setDispenseData({ ...dispenseData, customerName: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl"
                  placeholder="Enter farmer name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  value={dispenseData.customerPhone}
                  onChange={(e) => setDispenseData({ ...dispenseData, customerPhone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={dispenseData.notes}
                  onChange={(e) => setDispenseData({ ...dispenseData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl resize-none"
                  placeholder="Any notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowDispenseModal(false); setSelectedItem(null); }}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-indigo-700"
                >
                  Dispense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedItem.plantName}</h3>
                    <p className="text-indigo-100 text-sm">
                      {selectedItem.plantBrand}{selectedItem.plantVariety ? ` - ${selectedItem.plantVariety}` : ''}
                    </p>
                  </div>
                </div>
                <button onClick={() => { setShowDetailsModal(false); setSelectedItem(null); }} className="text-white/80 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-500 mb-1">Total Trays</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{selectedItem.totalTrays}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center">
                  <p className="text-sm text-green-600 mb-1">Available</p>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-300">{selectedItem.availableTrays}</p>
                </div>
              </div>

              {/* Dates */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Seeding Date</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{formatDate(selectedItem.seedingDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Expected Ready</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{formatDate(selectedItem.expectedReadyDate)}</p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Status:</span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedItem.status)}`}>
                  {getStatusIcon(selectedItem.status)}
                  {selectedItem.status.name}
                </span>
              </div>

              {/* Notes */}
              {selectedItem.notes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notes</h4>
                  <p className="text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                    {selectedItem.notes}
                  </p>
                </div>
              )}

              {/* Dispense History */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Dispense History</h4>
                {dispenseRecords.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No dispense records yet</p>
                ) : (
                  <div className="space-y-3">
                    {dispenseRecords.map((record) => (
                      <div key={record.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {record.traysDispensed} trays ({record.plantsDispensed} plants)
                            </p>
                            {record.customerName && (
                              <p className="text-sm text-gray-500">To: {record.customerName}</p>
                            )}
                            {record.notes && (
                              <p className="text-sm text-gray-400 mt-1">{record.notes}</p>
                            )}
                          </div>
                          <span className="text-sm text-gray-500">
                            {formatDate(record.dispenseDate)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Close Button */}
              <button
                onClick={() => { setShowDetailsModal(false); setSelectedItem(null); }}
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
